/**
 * RolesListPage
 * Page de liste et gestion des rôles
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Plus,
  Trash2,
  Edit,
  Users,
  Search,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  XCircle,
  Lock,
} from 'lucide-react';
import { useRoles, useSupprimerRole } from '../../infrastructure/api/useRoles';
import type { RoleDTO } from '../../application/dto/dtos';

export function RolesListPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleASupprimer, setRoleASupprimer] = useState<RoleDTO | null>(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: roles, isLoading, isError, refetch } = useRoles();
  const supprimerRole = useSupprimerRole();

  // Filtrage local
  const rolesFiltres: RoleDTO[] = roles?.filter((r: RoleDTO) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      r.nom.toLowerCase().includes(term) ||
      r.slug.toLowerCase().includes(term) ||
      (r.description?.toLowerCase().includes(term) ?? false)
    );
  }) ?? [];

  const handleSupprimer = async () => {
    if (!roleASupprimer) return;
    setSuppressionEnCours(true);
    setError(null);
    try {
      await supprimerRole.mutateAsync(roleASupprimer.idRole);
      setRoleASupprimer(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setSuppressionEnCours(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-600" />
            Gestion des rôles
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {roles?.length ?? 0} rôle{(roles?.length ?? 0) > 1 ? 's' : ''} configuré{(roles?.length ?? 0) > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/roles/new')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md
                   hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nouveau rôle
        </button>
      </div>

      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher un rôle..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                   bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200
                       dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* État de chargement */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Chargement des rôles...</span>
        </div>
      )}

      {/* Erreur de chargement */}
      {isError && (
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-600 dark:text-red-400">Erreur lors du chargement des rôles</p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Liste des rôles */}
      {!isLoading && !isError && (
        <div className="grid gap-4">
          {rolesFiltres.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              {searchTerm ? 'Aucun rôle correspondant à la recherche' : 'Aucun rôle configuré'}
            </div>
          ) : (
            rolesFiltres
              .sort((a: RoleDTO, b: RoleDTO) => a.niveau - b.niveau)
              .map((role: RoleDTO) => (
                <RoleCard
                  key={role.idRole}
                  role={role}
                  onEdit={() => navigate(`/admin/roles/${role.idRole}/edit`)}
                  onDelete={() => setRoleASupprimer(role)}
                />
              ))
          )}
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {roleASupprimer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Supprimer le rôle
              </h3>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Êtes-vous sûr de vouloir supprimer le rôle{' '}
              <strong>{roleASupprimer.nom}</strong> ?
            </p>
            {(roleASupprimer.nombreUtilisateurs ?? 0) > 0 && (
              <p className="text-amber-600 dark:text-amber-400 text-sm mb-4">
                ⚠️ Ce rôle est attribué à{' '}
                {roleASupprimer.nombreUtilisateurs} utilisateur
                {roleASupprimer.nombreUtilisateurs! > 1 ? 's' : ''}.
                La suppression est impossible tant que des utilisateurs l'utilisent.
              </p>
            )}

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setRoleASupprimer(null)}
                disabled={suppressionEnCours}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600
                         text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50
                         dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSupprimer}
                disabled={suppressionEnCours || (roleASupprimer.nombreUtilisateurs ?? 0) > 0}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md
                         hover:bg-red-700 transition-colors disabled:opacity-50
                         flex items-center justify-center gap-2"
              >
                {suppressionEnCours ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  'Supprimer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Composant carte rôle
// ============================================================

interface RoleCardProps {
  role: RoleDTO;
  onEdit: () => void;
  onDelete: () => void;
}

function RoleCard({ role, onEdit, onDelete }: RoleCardProps) {
  const [expanded, setExpanded] = useState(false);

  const niveauColors: Record<number, string> = {
    1: 'bg-red-100 text-red-700 border-red-200',
    2: 'bg-purple-100 text-purple-700 border-purple-200',
    3: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    4: 'bg-violet-100 text-violet-700 border-violet-200',
    5: 'bg-blue-100 text-blue-700 border-blue-200',
    6: 'bg-green-100 text-green-700 border-green-200',
    7: 'bg-gray-100 text-gray-700 border-gray-200',
  };

  const niveauColor = niveauColors[role.niveau] ?? 'bg-gray-100 text-gray-700 border-gray-200';

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border ${
      role.isActive
        ? 'border-gray-200 dark:border-gray-700'
        : 'border-dashed border-gray-300 dark:border-gray-600 opacity-75'
    } overflow-hidden`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          {/* Infos principales */}
          <div className="flex items-center gap-3 min-w-0">
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold
                         border ${niveauColor} flex-shrink-0`}
            >
              N{role.niveau}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {role.nom}
                </h3>
                {role.isSystemRole && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100
                                 text-yellow-700 rounded text-xs flex-shrink-0">
                    <Lock className="w-3 h-3" />
                    Système
                  </span>
                )}
                {!role.isActive && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs flex-shrink-0">
                    Inactif
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                  {role.slug}
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Users className="w-3 h-3" />
                  {role.nombreUtilisateurs ?? 0} utilisateur{(role.nombreUtilisateurs ?? 0) > 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Shield className="w-3 h-3" />
                  {role.permissions.length} permission{role.permissions.length > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
                       rounded transition-colors"
              title="Voir les permissions"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button
              onClick={onEdit}
              className="p-1.5 text-blue-500 hover:text-blue-700 rounded transition-colors"
              title="Modifier"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              disabled={role.isSystemRole}
              className="p-1.5 text-red-500 hover:text-red-700 rounded transition-colors
                       disabled:opacity-30 disabled:cursor-not-allowed"
              title={role.isSystemRole ? 'Rôle système non supprimable' : 'Supprimer'}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Description */}
        {role.description && (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {role.description}
          </p>
        )}
      </div>

      {/* Permissions (expandable) */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4">
          {role.permissions.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              Aucune permission assignée
            </p>
          ) : (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
                Permissions ({role.permissions.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {role.permissions.map((perm) => (
                  <span
                    key={perm.idPermission}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800
                             border border-gray-200 dark:border-gray-600 rounded text-xs
                             text-gray-700 dark:text-gray-300"
                    title={perm.description}
                  >
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                    {perm.slug}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
